import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import { styles } from '../../../../styles/themes/style';
import {
  ICampaign,
  ICampaignLead,
  ISequenceStep,
  DEFAULT_AI_LANGUAGE,
  getLeadStepContentOverride,
  isPerLeadAiComment,
  leadHasStepContentOverride,
  resolveDefaultStepContent,
  renderCampaignTemplate,
} from '../../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useAppActions } from '../../../../stores/application/useAppActions';
import SvgFragment from '../../../../fragments/SvgFragment';

const STEP_LABELS: Record<string, string> = {
  message: 'Direct message',
  post_comment: 'Post comment',
  connection_request: 'Connection note',
};

interface Props {
  show: boolean;
  onHide: () => void;
  campaign: ICampaign;
  lead: ICampaignLead;
  stepIndex: number;
  step: ISequenceStep;
  onSaved: (lead: ICampaignLead) => void;
}

const LeadMessageEditModal: React.FC<Props> = ({
  show, onHide, campaign, lead, stepIndex, step, onSaved,
}) => {
  const { updateLeadContentOverride, generateCampaignMessage } = useCampaignsRequest();
  const { setToast } = useAppActions();
  const { store: { user } } = useAuthStore();

  const senderName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const stepLabel = STEP_LABELS[step.type] || 'Message';
  const hasOverride = leadHasStepContentOverride(lead, stepIndex);

  const defaultResolved = useMemo(
    () => resolveDefaultStepContent(step, lead, senderName, campaign.variableFallbacks ?? {}),
    [step, lead, senderName, campaign.variableFallbacks],
  );

  const initialText = useMemo(() => {
    const override = getLeadStepContentOverride(lead, stepIndex);
    const fallbacks = campaign.variableFallbacks ?? {};
    if (override?.trim()) return renderCampaignTemplate(override, lead, senderName, fallbacks);
    return defaultResolved;
  }, [lead, stepIndex, defaultResolved, senderName, campaign.variableFallbacks]);

  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isCustom, setIsCustom] = useState(hasOverride);

  useEffect(() => {
    if (!show) return;
    setText(initialText);
    setIsCustom(hasOverride);
  }, [show, initialText, hasOverride]);

  // AI comments are always generated per lead, so a stale finalContent on the step
  // (older campaigns) must not hide the generate action — see isPerLeadAiComment.
  const isAiWithoutLock = isPerLeadAiComment(step) || (
    !step.finalContent?.trim() && (
      (step.type === 'message' && step.messageMode === 'ai')
      || (step.type === 'connection_request' && step.messageMode === 'ai')
    )
  );

  const handleGenerate = async () => {
    const prompt = step.type === 'post_comment'
      ? (step.commentAiPrompt || '')
      : (step.messageAiPrompt || step.messageTemplate || '');
    const renderedPrompt = renderCampaignTemplate(prompt, lead, senderName, campaign.variableFallbacks ?? {});
    if (!renderedPrompt.trim()) {
      setToast({ type: 'error', message: 'No AI prompt configured for this step.' });
      return;
    }
    setGenerating(true);
    try {
      const res: any = await generateCampaignMessage(campaign._id, lead._id, {
        stepType: step.type === 'post_comment' ? 'post_comment' : 'message',
        content: renderedPrompt,
        language: step.aiLanguage || DEFAULT_AI_LANGUAGE,
      });
      if (res?.generated && res?.content) {
        setText(res.content);
        setIsCustom(true);
      } else {
        setToast({ type: 'error', message: res?.error || 'AI generation failed.' });
      }
    } catch {
      setToast({ type: 'error', message: 'AI generation failed.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setText(defaultResolved);
    setIsCustom(false);
  };

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setToast({ type: 'error', message: 'Message cannot be empty.' });
      return;
    }

    const sameAsDefault = !isCustom || trimmed === defaultResolved.trim();
    setSaving(true);
    try {
      const res: any = await updateLeadContentOverride(campaign._id, lead._id, {
        stepIndex,
        content: sameAsDefault ? null : trimmed,
      });
      if (res?.lead) {
        onSaved(res.lead);
        setToast({ type: 'success', message: sameAsDefault ? 'Using campaign default for this lead.' : 'Custom message saved for this lead.' });
        onHide();
      } else {
        setToast({ type: 'error', message: res?.message || 'Failed to save message.' });
      }
    } catch {
      setToast({ type: 'error', message: 'Failed to save message.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledModal show={show} onHide={onHide} width="560px" backdrop>
      <ModalBody>
        <ModalHeader>
          <div>
            <ModalTitle>{stepLabel} for {lead.name || 'lead'}</ModalTitle>
            <ModalSub>
              {[lead.title, lead.company].filter(Boolean).join(' · ') || '—'}
            </ModalSub>
          </div>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </ModalHeader>

        <InfoLine>
          {isCustom
            ? 'Custom message — only this lead will receive this text.'
            : 'Campaign default — edit to customize for this lead only.'}
        </InfoLine>

        <MessageArea
          value={text}
          onChange={e => { setText(e.target.value); setIsCustom(true); }}
          rows={8}
          placeholder="Message text…"
          disabled={generating}
        />

        <BtnRow>
          {isAiWithoutLock && (
            <SecondaryBtn type="button" onClick={handleGenerate} disabled={generating || saving}>
              <SvgFragment type="sparkle" />
              {generating ? 'Generating…' : 'Generate for this lead'}
            </SecondaryBtn>
          )}
          {isCustom && (
            <SecondaryBtn type="button" onClick={handleReset} disabled={saving || generating}>
              Reset to default
            </SecondaryBtn>
          )}
        </BtnRow>

        <Footer>
          <FooterHint>
            Leave unchanged to send the campaign default to this lead.
          </FooterHint>
          <FooterActions>
            <SecondaryBtn type="button" onClick={onHide} disabled={saving}>Cancel</SecondaryBtn>
            <PrimaryBtn type="button" onClick={handleSave} disabled={saving || generating}>
              {saving ? 'Saving…' : 'Save'}
            </PrimaryBtn>
          </FooterActions>
        </Footer>
      </ModalBody>
    </StyledModal>
  );
};

export default LeadMessageEditModal;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-family: NunitoSans;
  font-size: 18px;
  font-weight: 800;
  color: ${styles.colors.black600};
`;

const ModalSub = styled.p`
  margin: 4px 0 0;
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black300};
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  color: ${styles.colors.black300};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  &:hover { background: ${styles.colors.black100}; color: ${styles.colors.black600}; }
`;

const InfoLine = styled.p`
  margin: 0 0 10px;
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.primary600};
  background: ${styles.colors.primary100};
  padding: 8px 10px;
  border-radius: 8px;
`;

const MessageArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: 12px 14px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 10px;
  font-family: NunitoSans;
  font-size: 14px;
  line-height: 1.5;
  color: ${styles.colors.black600};
  resize: vertical;
  margin-bottom: 12px;
  &:focus {
    outline: none;
    border-color: ${styles.colors.primary400};
    box-shadow: 0 0 0 2px ${styles.colors.primary100};
  }
  &:disabled { opacity: 0.6; background: ${styles.colors.black100}; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${styles.colors.black200};
`;

const FooterHint = styled.p`
  margin: 0;
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.black300};
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const BtnBase = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled(BtnBase)`
  background: ${styles.colors.primary600};
  color: ${styles.colors.white};
  &:hover:not(:disabled) { filter: brightness(0.92); }
`;

const SecondaryBtn = styled(BtnBase)`
  background: ${styles.colors.white};
  color: ${styles.colors.black600};
  border: 1px solid ${styles.colors.black200};
  &:hover:not(:disabled) { background: ${styles.colors.black100}; }
`;
